import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Card, CardHeader, CardContent, InputLabel, Typography, Divider, } from '@mui/material';
import { compareAsc } from 'date-fns';
import DeliveryRequestDatePicker from './DeliveryRequestDatePicker';
import DeliveryRequestItems from './DeliveryRequestItems';
import DeliveryRequestItemsAndRequestDateDialog from './DeliveryRequestItemsAndRequestDateDialog';
var DeliveryRequestItemsAndRequestDateForm = function (_a) {
    var _b;
    var roaster = _a.roaster, deliveryRequestFormType = _a.deliveryRequestFormType, deliveryRequestItemsAndRequestDates = _a.deliveryRequestItemsAndRequestDates, _c = _a.maxInstallmentDeliveryRequestItemsAndRequestDatesLength, maxInstallmentDeliveryRequestItemsAndRequestDatesLength = _c === void 0 ? 3 : _c, requestItemsWithRequestableQuantity = _a.requestItemsWithRequestableQuantity, onChangeDeliveryRequestFormType = _a.onChangeDeliveryRequestFormType, addDeliveryRequestItemsAndRequestDate = _a.addDeliveryRequestItemsAndRequestDate, updateDeliveryRequestItemsAndRequestDate = _a.updateDeliveryRequestItemsAndRequestDate, deleteDeliveryRequestItemsAndRequestDate = _a.deleteDeliveryRequestItemsAndRequestDate, isAdmin = _a.isAdmin;
    var t = useTranslation().t;
    var _d = React.useState(false), dialogOpen = _d[0], setDialogOpen = _d[1];
    var _e = React.useState(), editItemsAndRequestDate = _e[0], setEditItemsAndRequestDate = _e[1];
    var nextDeliveryRequestFormType = React.useMemo(function () {
        return deliveryRequestFormType === 'oneTime' ? 'installment' : 'oneTime';
    }, [deliveryRequestFormType]);
    var warehousingRequestItems = React.useMemo(function () {
        return requestItemsWithRequestableQuantity.filter(function (_a) {
            var roasterTransportToWarehousesStatus = _a.roasterTransportToWarehousesStatus;
            return roasterTransportToWarehousesStatus === 'warehousing';
        });
    }, [requestItemsWithRequestableQuantity]);
    var warehousingRequestItemsQuantity = React.useMemo(function () {
        return warehousingRequestItems.reduce(function (accumulator, _a) {
            var quantity = _a.quantity;
            return accumulator + quantity;
        }, 0);
    }, [warehousingRequestItems]);
    var warehousingRequestItemsAttributes = React.useMemo(function () {
        return warehousingRequestItems.map(function (_a) {
            var orderedItemId = _a.orderedItemId, warehouseItemId = _a.warehouseItemId, lotNumber = _a.lotNumber, bagSize = _a.bagSize, requestableQuantity = _a.requestableQuantity;
            return ({
                orderedItemId: orderedItemId,
                warehouseItemId: warehouseItemId,
                lotNumber: lotNumber,
                bagSize: bagSize,
                quantity: requestableQuantity,
            });
        });
    }, [warehousingRequestItems]);
    var oneTimeDeliveryRequestItemsAndRequestDate = React.useMemo(function () {
        return deliveryRequestItemsAndRequestDates.find(function (_a) {
            var key = _a.key;
            return key === 'oneTime';
        });
    }, [deliveryRequestItemsAndRequestDates]);
    var installmentDeliveryRequestItemsAndRequestDates = React.useMemo(function () {
        return deliveryRequestItemsAndRequestDates
            .filter(function (_a) {
            var key = _a.key;
            return key !== 'oneTime';
        })
            .sort(function (a, b) {
            if (!a.requestDate)
                return -1;
            if (!b.requestDate)
                return 1;
            return compareAsc(new Date(a.requestDate), new Date(b.requestDate));
        });
    }, [deliveryRequestItemsAndRequestDates]);
    var isLastInstallmentDeliveryRequestItemsAndRequestDate = React.useMemo(function () {
        return (installmentDeliveryRequestItemsAndRequestDates.length +
            (editItemsAndRequestDate ? 0 : 1) >=
            maxInstallmentDeliveryRequestItemsAndRequestDatesLength);
    }, [
        installmentDeliveryRequestItemsAndRequestDates,
        editItemsAndRequestDate,
        maxInstallmentDeliveryRequestItemsAndRequestDatesLength,
    ]);
    var upsertOneTimeDeliveryRequestItemsAndRequestDate = React.useCallback(function (deliveryRequestGroupItemsAndRequestDate) {
        oneTimeDeliveryRequestItemsAndRequestDate
            ? updateDeliveryRequestItemsAndRequestDate(deliveryRequestGroupItemsAndRequestDate)
            : addDeliveryRequestItemsAndRequestDate(deliveryRequestGroupItemsAndRequestDate);
    }, [
        updateDeliveryRequestItemsAndRequestDate,
        addDeliveryRequestItemsAndRequestDate,
        oneTimeDeliveryRequestItemsAndRequestDate,
    ]);
    var handleOpenDialog = React.useCallback(function (itemsAndRequestDate) {
        setEditItemsAndRequestDate(itemsAndRequestDate);
        setDialogOpen(true);
    }, [setEditItemsAndRequestDate, setDialogOpen]);
    var isFirstDeliveryEditing = React.useMemo(function () {
        if (deliveryRequestFormType === 'oneTime')
            return true;
        var installmentDeliveryRequests = deliveryRequestItemsAndRequestDates.filter(function (item) { return item.key !== 'oneTime'; });
        if (!editItemsAndRequestDate) {
            if (installmentDeliveryRequests.length === 0)
                return true;
            return false;
        }
        if (deliveryRequestItemsAndRequestDates.length === 0)
            return false;
        var sortedItems = installmentDeliveryRequests.sort(function (a, b) {
            if (!a.requestDate)
                return -1;
            if (!b.requestDate)
                return 1;
            return a.requestDate.localeCompare(b.requestDate);
        });
        return editItemsAndRequestDate.key === sortedItems[0].key;
    }, [
        deliveryRequestFormType,
        editItemsAndRequestDate,
        deliveryRequestItemsAndRequestDates,
    ]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Box, { sx: {
                border: 'solid 1px rgba(145, 158, 171, 0.32)',
                '&:hover': { border: 'solid 1px rgba(0, 0, 0, 0.87)' },
                borderRadius: 1,
                position: 'relative',
                marginBottom: 1,
            } },
            React.createElement(Box, { display: "flex", justifyContent: "space-between", alignItems: "center" },
                React.createElement(InputLabel, { focused: true, shrink: true, id: "delivery-request-date", sx: {
                        backgroundColor: function (theme) {
                            return theme.customPalette.background.default;
                        },
                        color: function (theme) { return theme.customPalette.text.secondary; },
                        position: 'absolute',
                        left: '12px',
                        top: '-8px',
                        padding: '0 8px',
                        zIndex: '1',
                    } }, t('pages.deliveryRequestItemsAndRequestDateForm.title'))),
            React.createElement(Card, { sx: { boxShadow: 'none' } },
                React.createElement(CardHeader, { title: React.createElement(Box, { sx: { typography: function (theme) { return theme.customTypography.subtitle2; } } }, t("pages.deliveryRequestItemsAndRequestDateForm.type.".concat(deliveryRequestFormType))), subheader: React.createElement(React.Fragment, null,
                        !isAdmin && (React.createElement(Box, { sx: { typography: function (theme) { return theme.customTypography.body3; } } }, t("pages.deliveryRequestItemsAndRequestDateForm.subheaders.".concat(deliveryRequestFormType)))),
                        deliveryRequestFormType === 'installment' && (React.createElement(Box, { sx: { typography: function (theme) { return theme.customTypography.body3; } } }, t('pages.deliveryRequestItemsAndRequestDateForm.subheaders.installmentCaption')))), action: React.createElement(Box, { display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }, warehousingRequestItemsQuantity >= 2 &&
                        onChangeDeliveryRequestFormType && (React.createElement(Button, { color: "success", variant: "outlined", size: "small", onClick: function () {
                            return onChangeDeliveryRequestFormType(nextDeliveryRequestFormType);
                        }, sx: { fontWeight: 'bold' } }, t('pages.deliveryRequestItemsAndRequestDateForm.changeTimings', {
                        timing: t("pages.deliveryRequestItemsAndRequestDateForm.type.".concat(nextDeliveryRequestFormType)),
                    })))) }),
                React.createElement(Divider, { sx: { py: 1, mx: 3 } }),
                React.createElement(CardContent, null, deliveryRequestFormType === 'oneTime' ? (React.createElement(React.Fragment, null,
                    React.createElement(DeliveryRequestDatePicker, { roaster: roaster, fastestSelectable: true, value: (_b = oneTimeDeliveryRequestItemsAndRequestDate === null || oneTimeDeliveryRequestItemsAndRequestDate === void 0 ? void 0 : oneTimeDeliveryRequestItemsAndRequestDate.requestDate) !== null && _b !== void 0 ? _b : null, onChange: function (requestDate, isFastestDelivery) {
                            upsertOneTimeDeliveryRequestItemsAndRequestDate({
                                id: oneTimeDeliveryRequestItemsAndRequestDate === null || oneTimeDeliveryRequestItemsAndRequestDate === void 0 ? void 0 : oneTimeDeliveryRequestItemsAndRequestDate.id,
                                key: 'oneTime',
                                itemsAttributes: (oneTimeDeliveryRequestItemsAndRequestDate === null || oneTimeDeliveryRequestItemsAndRequestDate === void 0 ? void 0 : oneTimeDeliveryRequestItemsAndRequestDate.itemsAttributes) ||
                                    warehousingRequestItemsAttributes,
                                requestDate: requestDate,
                                isFastestDelivery: isFastestDelivery,
                            });
                        } }))) : (React.createElement(React.Fragment, null,
                    React.createElement(DeliveryRequestItems, { itemsAndRequestDates: installmentDeliveryRequestItemsAndRequestDates, handleEditItemsAndRequestDate: function (itemsAndRequestDate) {
                            return handleOpenDialog(itemsAndRequestDate);
                        }, handleDeleteItemsAndRequestDate: deleteDeliveryRequestItemsAndRequestDate }),
                    requestItemsWithRequestableQuantity &&
                        requestItemsWithRequestableQuantity.reduce(function (accumulator, _a) {
                            var requestableQuantity = _a.requestableQuantity;
                            return accumulator + requestableQuantity;
                        }, 0) > 0 && (React.createElement(Button, { color: "success", variant: "contained", size: "small", onClick: function () { return handleOpenDialog(undefined); }, sx: { color: '#FFF' } }, t("pages.deliveryRequestItemsAndRequestDateForm.buttons.".concat(deliveryRequestItemsAndRequestDates.filter(function (_a) {
                        var key = _a.key;
                        return key !== 'oneTime';
                    }).length > 0
                        ? 'add'
                        : 'new'))))))))),
        React.createElement(Typography, { variant: "caption" }, t('pages.deliveryRequestItemsAndRequestDateForm.captions.card')),
        requestItemsWithRequestableQuantity && (React.createElement(DeliveryRequestItemsAndRequestDateDialog, { roaster: roaster, open: dialogOpen, onClose: function () { return setDialogOpen(false); }, isFirstDelivery: isFirstDeliveryEditing, installmentDeliveryRequestItemsAndRequestDates: installmentDeliveryRequestItemsAndRequestDates, requestItemsWithRequestableQuantity: requestItemsWithRequestableQuantity, editItemsAndRequestDate: editItemsAndRequestDate, isLastInstallmentDeliveryRequestItemsAndRequestDate: isLastInstallmentDeliveryRequestItemsAndRequestDate, addDeliveryRequestItemsAndRequestDate: addDeliveryRequestItemsAndRequestDate, updateDeliveryRequestItemsAndRequestDate: updateDeliveryRequestItemsAndRequestDate }))));
};
export default DeliveryRequestItemsAndRequestDateForm;
